import { useEffect, useState } from "react";

import {
  type ApiDeps,
  type SovereigntyAxis,
  type SovereigntyView,
  fetchSovereignty,
} from "../lib/api";
import { t, tn, type Language, type StringKey } from "../lib/i18n";

interface SovereigntyPaneProps {
  deps: ApiDeps;
  language: Language;
  /** Shared refresh signal — bump to refetch. Defaults to 0 so the
   * component renders standalone (e.g. in unit tests). */
  reloadKey?: number;
  /** Activity-window length in days from the range picker. Defaults to 30.
   * (Enablement + scope columns are point-in-time; only the activity
   * counts depend on this.) */
  periodDays?: number;
  /** Localized active-range label (e.g. "30 dias" / "Este ano") used in the
   * range-aware activity-count column header — so the header can no longer
   * say "30 days" while showing a 7d/90d/all-time count (F37 §12.3 #4).
   * Defaults to the 30-day label for standalone renders. */
  rangeLabel?: string;
}

type PaneState =
  | { kind: "loading" }
  | { kind: "loaded"; view: SovereigntyView }
  | { kind: "error" };

// Friendly customer-facing label per technical axis name. Falls back to
// the raw axis_name if a future axis lands without a label (the i18n
// parity test guards the keys themselves).
const _AXIS_LABEL: Record<string, StringKey> = {
  "mail.send": "sovereignty.axis.mail.send",
  "sharepoint.write": "sovereignty.axis.sharepoint.write",
  "planner.create_task": "sovereignty.axis.planner.create_task",
  "planner.move_bucket": "sovereignty.axis.planner.move_bucket",
  "teams.channel_post": "sovereignty.axis.teams.channel_post",
  "llm.drafter": "sovereignty.axis.llm.drafter",
  "llm.triage": "sovereignty.axis.llm.triage",
  "llm.qa_loop": "sovereignty.axis.llm.qa_loop",
};

const _SCOPE_LABEL: Record<string, StringKey> = {
  "sharepoint.sites": "sovereignty.scope.sharepoint.sites",
  "sharepoint.libraries": "sovereignty.scope.sharepoint.libraries",
  "planner.plans": "sovereignty.scope.planner.plans",
  "teams.teams": "sovereignty.scope.teams.teams",
  "teams.channels": "sovereignty.scope.teams.channels",
  "mail.mailboxes": "sovereignty.scope.mail.mailboxes",
  "mail.excluded_folders": "sovereignty.scope.mail.excluded_folders",
};

/** Sovereignty pane (per F37 spec §5.2 row 7). Joins the capability
 * profile (what the agent is *allowed* to do) with the audit log (what
 * it *has* done) — the customer-facing data-sovereignty story. The
 * server decides enablement + scope from the per-tenant profile; this
 * component renders whatever it's given. */
export function SovereigntyPane({
  deps,
  language,
  reloadKey = 0,
  periodDays = 30,
  rangeLabel,
}: SovereigntyPaneProps): JSX.Element {
  const [state, setState] = useState<PaneState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetchSovereignty(deps, periodDays)
      .then((view) => {
        if (!cancelled) setState({ kind: "loaded", view });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [deps.backendUrl, deps.getAuthToken, reloadKey, periodDays]);

  return (
    <section
      aria-label={t("sovereignty.heading", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <h2 className="mb-1 text-base font-semibold text-kwanza-ink">
        {t("sovereignty.heading", language)}
      </h2>
      <p className="mb-3 text-sm text-kwanza-inkSoft">
        {t("sovereignty.intro", language)}
      </p>
      {state.kind === "loading" ? (
        <div
          className="h-24 animate-pulse rounded bg-kwanza-divider"
          aria-busy="true"
          data-testid="sovereignty-skeleton"
        />
      ) : state.kind === "error" ? (
        <p className="text-sm text-kwanza-alert" data-testid="sovereignty-error">
          {t("error.fetch_failed.body", language)}
        </p>
      ) : (
        <SovereigntyBody
          view={state.view}
          language={language}
          rangeLabel={rangeLabel ?? t("range.30d", language)}
        />
      )}
    </section>
  );
}

function _axisLabel(axis: SovereigntyAxis, language: Language): string {
  const key = _AXIS_LABEL[axis.axis_name];
  return key ? t(key, language) : axis.axis_name;
}

function SovereigntyBody({
  view,
  language,
  rangeLabel,
}: {
  view: SovereigntyView;
  language: Language;
  rangeLabel: string;
}): JSX.Element {
  const anyEnabled = view.axes.some((a) => a.enabled);
  const capabilityHeader = t("sovereignty.col.capability", language);
  const statusHeader = t("sovereignty.col.status", language);
  const lastActivityHeader = t("sovereignty.col.last_activity", language);
  const countHeader = tn("sovereignty.col.count", { range: rangeLabel }, language);
  return (
    <div data-testid="sovereignty-body">
      {/* `reflow-card` reflows this table to stacked cards below `sm`
       * (tailwind.css) by overriding `display`, which strips the implicit
       * table roles from the a11y tree — so we restore them with EXPLICIT
       * role="table/rowgroup/row/columnheader/cell" (jsx-a11y/no-redundant-
       * roles is scoped off for this file in .eslintrc). Below `sm` the
       * header is `display:none` and each cell's `data-label` ::before
       * carries the column name. */}
      <table role="table" className="reflow-card w-full text-sm">
        <thead role="rowgroup">
          <tr role="row" className="text-left text-xs uppercase tracking-wide text-kwanza-inkSoft">
            <th role="columnheader" scope="col" className="py-1 font-medium">
              {capabilityHeader}
            </th>
            <th role="columnheader" scope="col" className="py-1 font-medium">
              {statusHeader}
            </th>
            <th role="columnheader" scope="col" className="py-1 font-medium">
              {lastActivityHeader}
            </th>
            <th role="columnheader" scope="col" className="py-1 text-right font-medium">
              {countHeader}
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup" className="divide-y divide-kwanza-divider">
          {view.axes.map((axis) => (
            <tr role="row" key={axis.axis_name} data-testid="sovereignty-axis-row">
              <td role="cell" data-label={capabilityHeader} className="py-1.5 text-kwanza-ink">
                {_axisLabel(axis, language)}
              </td>
              <td role="cell" data-label={statusHeader} className="py-1.5">
                <StatusChip enabled={axis.enabled} language={language} />
              </td>
              <td
                role="cell"
                data-label={lastActivityHeader}
                className="py-1.5 text-kwanza-inkSoft"
              >
                {axis.last_activity
                  ? axis.last_activity.slice(0, 10)
                  : t("sovereignty.never", language)}
              </td>
              <td
                role="cell"
                data-label={countHeader}
                className="py-1.5 text-right font-medium text-kwanza-ink"
              >
                {axis.count_30d}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!anyEnabled ? (
        <p
          className="mt-3 text-sm text-kwanza-inkSoft"
          data-testid="sovereignty-empty"
        >
          {t("sovereignty.empty", language)}
        </p>
      ) : null}
      <ScopeSummary view={view} language={language} />
    </div>
  );
}

function StatusChip({
  enabled,
  language,
}: {
  enabled: boolean;
  language: Language;
}): JSX.Element {
  // The leading glyph is a non-colour cue (A4 / WCAG 1.4.1) so enabled vs
  // disabled isn't distinguished by hue alone; aria-hidden because the text
  // label already conveys the state.
  return enabled ? (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-kwanza-accentSoft px-2 py-0.5 text-xs font-semibold text-kwanza-accentText"
      data-testid="sovereignty-chip-enabled"
    >
      <span aria-hidden="true">✓</span>
      {t("sovereignty.enabled", language)}
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-kwanza-divider px-2 py-0.5 text-xs font-medium text-kwanza-inkSoft"
      data-testid="sovereignty-chip-disabled"
    >
      <span aria-hidden="true">○</span>
      {t("sovereignty.disabled", language)}
    </span>
  );
}

function ScopeSummary({
  view,
  language,
}: {
  view: SovereigntyView;
  language: Language;
}): JSX.Element | null {
  // Only surface scope dimensions that are actually bounded (count > 0);
  // a zero count means "unscoped / not applicable" and would read as
  // noise in the customer-facing summary.
  const bounded = Object.entries(view.scope_counts).filter(([, n]) => n > 0);
  if (bounded.length === 0) return null;
  return (
    <div className="mt-3 border-t border-kwanza-divider pt-3" data-testid="sovereignty-scope">
      <span className="text-xs uppercase tracking-wide text-kwanza-inkSoft">
        {t("sovereignty.scope.heading", language)}
      </span>
      <p className="mt-1 text-sm text-kwanza-ink">
        {t("sovereignty.scope.intro", language)}{" "}
        {bounded
          .map(([name, n]) => {
            const key = _SCOPE_LABEL[name];
            return `${n} ${key ? t(key, language) : name}`;
          })
          .join(" · ")}
      </p>
    </div>
  );
}
